<template>
	<VTooltip ref="vTooltip" v-model:shown="tooltipShow" :triggers="[]" :placement="tooltipPlacement">
		<template #popper>
			<component :is="tooltipContent" v-if="typeof tooltipContent === 'function' || typeof tooltipContent === 'object'" />
			<span style="white-space: pre-wrap" v-html="tooltipContent" v-else></span>
		</template>
	</VTooltip>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { oneTooltipInstanceExists, useOneTooltip } from './useOneTooltip'

defineOptions({
	name: 'OneTooltip',
})

type VTooltip = {
	$refs: {
		popper: {
			$_referenceNode: HTMLElement
			$_detachPopperNode: () => void
			isShown: boolean
			$emit: (event: string, ...args: unknown[]) => void
		}
	}
}

const nextFrame = () => {
	return new Promise((resolve) =>
		requestAnimationFrame(() => {
			requestAnimationFrame(resolve)
		}),
	)
}

const vTooltip = ref<VTooltip | null>(null)
const tooltipShow = ref(false)
const { tooltipContent, tooltipPlacement, shouldTooltipShow, tooltipTrigger } = useOneTooltip()

// Shows or hides the tooltip
const updateReference = async (newReference: HTMLElement | null) => {
	const popper = vTooltip.value?.$refs?.popper
	const hasNewReference = newReference !== null

	if (popper) {
		// Detach the popper node
		popper.$_detachPopperNode()

		// If the tooltip is shown, hide it
		if (hasNewReference && popper.isShown) {
			popper.$emit('update:shown', false)
		}

		// Wait for the popper to detach the popper node
		await nextFrame()

		popper.$_referenceNode = hasNewReference ? newReference : document.body

		// Wait for the popper to update the reference
		await nextFrame()

		popper.$emit('update:shown', hasNewReference)
	}
}

// Watch for the tooltip's show state and show or hide the tooltip accordingly
watch(shouldTooltipShow, async (nV) => {
	if (nV && tooltipTrigger.value) {
		updateReference(tooltipTrigger.value)
	} else {
		updateReference(null)
	}
})

// Check if the OneTooltip instance already exists
if (oneTooltipInstanceExists.value) {
	throw new Error('OneTooltip can only be used once per application. Remove duplicate instances.')
}

oneTooltipInstanceExists.value = true
</script>
